<template>
  <div
    :class="[
      segmented ? '' : currentClass,
      { 'border' : outline},
      { 'opacity-75' : disabled }
    ]"
  >
    <div class="relative flex items-center h-full w-full" :class="segmented ? 'space-x-1' : ''">
      <!-- pill -->
      <div
        v-if="!segmented && selectedIndex !== -1 && currentValue !== undefined"
        :class="pillClass"
        :style="pillStyle"
      />

      <!-- segments -->
      <template v-for="(option, index) in normalizedOptions" :key="`${ id || name || key }-${index}`">
        <component
          :is="option.tooltip ? 'o-tooltip' : 'div'"
          :ref="`segment-${index}`"

          :text="option.tooltip"
          class="min-w-0 flex-grow relative"
          :class="segmented ? 'flex bg-gray-100 dark:bg-gray-900 border dark:border-gray-800 rounded-lg p-1 h-9 focus-within:ring-2' : ''"
        >
          <div
            class="relative min-w-0 flex-grow flex text-sm items-center justify-center h-full border-r border-gray-200 dark:border-gray-800 last:border-r-0 py-1"
            :class="[
              disabled || option.disabled ? 'opacity-75 cursor-not-allowed' : 'hover:text-primary-600 cursor-pointer dark:hover:text-primary-300',
              currentValue === option.value ? activeClass + ' bg-white dark:bg-gray-800 shadow rounded-md' : 'opacity-80',
              sizeClass,
            ]"
            @click="handleOptionClick(option)"
          >
            <template v-if="option.icon">
              <o-icon :icon="option.icon" />
            </template>

            <template v-else>
              <span class="block min-w-0 truncate whitespace-nowrap">
                {{ option.text }}
              </span>
            </template>
          </div>
        </component>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { getCurrentInstance } from 'vue'
import get from 'lodash/get'

interface Props {
  modelValue?: string | number | boolean | Record<string, any> | null
  name?: string | null
  id?: string | null
  baseClass?: string | Record<string, any> | string[]
  activeClass?: string | Record<string, any> | string[]
  pillClass?: string | Record<string, any> | string[]
  defaultStatusClass?: string | Record<string, any> | string[]
  warningStatusClass?: string | Record<string, any> | string[]
  errorStatusClass?: string | Record<string, any> | string[]
  successStatusClass?: string | Record<string, any> | string[]
  sizeClass?: string | Record<string, any> | string[]
  outline?: boolean
  disabled?: boolean
  segmented?: boolean
  // multi mixin
  valueAttribute?: string
  textAttribute?: string
  options?: any[] | Record<string, any>
  // classes mixin
  size?: string | null
  status?: boolean | string | null
  disabledClass?: string | Record<string, any> | string[]
  defaultSizeClass?: string | Record<string, any> | string[]
  largeSizeClass?: string | Record<string, any> | string[]
  smallSizeClass?: string | Record<string, any> | string[]
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: null,
  name: null,
  id: null,
  baseClass: 'flex bg-gray-100 dark:bg-gray-900 dark:border dark:border-gray-800 rounded-lg p-1 h-9 focus-within:ring-2',
  activeClass: 'text-primary-600 active:text-primary-600 dark:text-primary-300 dark:active:text-primary-300',
  pillClass: 'absolute left-0 inset-y-0 flex bg-white dark:bg-gray-800 shadow transition-all ease-in-out duration-200 rounded-md',
  defaultStatusClass: '',
  warningStatusClass: 'border border-yellow-400 bg-yellow-100 dark:bg-yellow-800/20',
  errorStatusClass: 'border border-red-300 bg-red-100 dark:bg-red-800/20',
  successStatusClass: 'border border-green-300 bg-green-100 dark:bg-green-800/20',
  sizeClass: 'px-2',
  outline: false,
  disabled: false,
  segmented: false,
  valueAttribute: undefined,
  textAttribute: undefined,
  options: () => [],
  size: null,
  status: null,
  disabledClass: undefined,
  defaultSizeClass: undefined,
  largeSizeClass: undefined,
  smallSizeClass: undefined,
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: any): void
  (e: 'change', value: any): void
  (e: 'click', value: any): void
}>()

const instance = getCurrentInstance()

const key = ref(`${Date.now()}-${Math.random()}`)
const currentValue = ref<any>(props.modelValue)
const pillStyle = ref<Record<string, string> | null>(null)

// -- multi mixin --
function guessOptionValue (option: any) {
  if (props.valueAttribute) return get(option, props.valueAttribute)
  return get(option, 'value', get(option, 'id', get(option, 'text')))
}

function guessOptionText (option: any) {
  if (props.textAttribute) return get(option, props.textAttribute)
  return get(option, 'text', get(option, 'label'))
}

function normalizeOption (option: any): any {
  if (['string', 'number', 'boolean'].includes(typeof option)) {
    return { value: option, text: option }
  }
  return {
    value: guessOptionValue(option),
    text: guessOptionText(option),
    info: get(option, 'info', get(option, 'description')),
    icon: option.icon,
    disabled: option.disabled,
    tooltip: option.tooltip,
    class: option.class,
  }
}

const normalizedOptions = computed(() => {
  if (props.options) {
    if (Array.isArray(props.options)) {
      return props.options.map((option: any) => normalizeOption(option))
    } else {
      return Object.keys(props.options).map(k => ({
        value: k,
        text: (props.options as Record<string, any>)[k],
      }))
    }
  }
  return []
})

// -- classes mixin --
const noStatus = computed(() => props.status === null)
const isError = computed(() => props.status === false || props.status === 'error')
const isSuccess = computed(() => props.status === true || props.status === 'success')
const isWarning = computed(() => props.status === 'warning')

const statusClasses = computed(() => {
  const classes: any[] = []
  if (props.disabled) {
    if (props.disabledClass) classes.push(props.disabledClass)
  }
  if (props.size === null && props.defaultSizeClass) classes.push(props.defaultSizeClass)
  else if (props.size === 'sm' && props.smallSizeClass) classes.push(props.smallSizeClass)
  else if (props.size === 'lg' && props.largeSizeClass) classes.push(props.largeSizeClass)
  if (!props.disabled && noStatus.value && props.defaultStatusClass) classes.push(props.defaultStatusClass)
  if (isError.value && props.errorStatusClass) classes.push(props.errorStatusClass)
  else if (isSuccess.value && props.successStatusClass) classes.push(props.successStatusClass)
  else if (isWarning.value && props.warningStatusClass) classes.push(props.warningStatusClass)
  return classes
})

const currentClass = computed(() => {
  const classes: any[] = [props.baseClass]
  return classes.concat(statusClasses.value)
})

const selectedIndex = computed(() => {
  const opts = Array.isArray(props.options) ? props.options : []
  return opts.findIndex((segment: any) => segment.value === currentValue.value)
})

const segmentWidth = computed(() => {
  const opts = Array.isArray(props.options) ? props.options : []
  return 100 / opts.length
})

watch(() => props.modelValue, (value) => {
  currentValue.value = value
})

watch(currentValue, (val) => {
  emit('update:modelValue', val)
  emit('change', val)
})

watch(selectedIndex, () => {
  calcPillStyle()
}, { immediate: true })

onMounted(() => {
  if (!props.segmented) {
    calcPillStyle()

    setTimeout(() => {
      calcPillStyle()
    }, 500)
  }
})

function calcPillStyle () {
  const refs = instance?.proxy?.$refs
  const selected = refs?.[`segment-${selectedIndex.value}`] as any

  if (selected) {
    const segment = Array.isArray(selected) ? selected[0] : selected

    const transform = `translateX(${segment?.offsetLeft || 0}px)`
    const opacity = selectedIndex.value === -1 ? '0' : '1'
    const width = `${segment?.offsetWidth || 0}px`

    pillStyle.value = {
      transform,
      opacity,
      width,
    }
  } else {
    pillStyle.value = null
  }
}

function handleOptionClick (option: any) {
  if (!props.disabled && !option.disabled) {
    currentValue.value = option.value
  }

  emit('click', currentValue.value)
}
</script>

<style lang="scss">
  .segment-control {
    grid-auto-columns: 1fr;

    .option {

      &:active input:not(:checked) + label span {
        transform: scale(.95);
      }

      &:first-of-type {
        grid-column: 1;
        grid-row: 1;
        box-shadow: none;

        input {
          &::before
          {
            opacity: 0;
          }
        }
      }

      &:last-of-type {
        input {
          &::after
          {
            opacity: 0;
          }
        }
      }

      input {
        &:checked+label {
          cursor: default;

          &::before,
          &::after {
            background: var(--background);
            z-index: 1;
          }
        }

        &::before,
        &::after {
          content: '';
          width: 1px;
          background: rgba(142,142,147,.15) !important;
          position: absolute;
          top: 10%;
          bottom: 10%;
          border-radius: 0.25rem;
          will-change: background;
          -webkit-transition: background .2s ease;
          transition: background .2s ease;
        }

        &::before {
          left: 0;
          transform: translateX(-.5px);
        }

        &::after {
          right: 0;
          transform: translateX(.5px);
        }
      }
    }
  }
</style>
